import {DataList} from '@macmillanplatform/ui-core';

import type {
  ColumnDefinitionItem,
  PurchaseOrderItem,
  RawColumnDefinitionItem,
  RawPurchaseOrderItem,
  SectionContent,
} from '../types';

import {formatters} from '../components/formatters';
import type {AccordionPageSection} from '@/components/accordion-page';

export const SECTIONS_TYPES = {
  PURCHASE_ORDER: 'purchase-order',
  COLUMNS_DEFINITION: 'columns-definition',
};

export const SECTIONS_CONTENT: Record<
  string,
  Array<RawPurchaseOrderItem | RawColumnDefinitionItem>
> = {
  [SECTIONS_TYPES.PURCHASE_ORDER]: [
    {
      value:
        'Enter PO and then select <b>Search</b> - optional ISBN and Ship To.',
    },
    {
      value:
        'Enter ISBN and then select <b>Search</b> - optional PO and Ship To.',
    },
    {
      value:
        'Enter Ship To and then select <b>Search</b> - optional PO and ISBN.',
    },
  ],
  [SECTIONS_TYPES.COLUMNS_DEFINITION]: [
    {
      label: '<b>Author:</b>',
      value: 'Primary author for book only.',
    },
    {
      label: '<b>Backorder Units:</b>',
      value:
        'Units from a customer order that are on backorder plus units that are being processed.',
    },
    {
      label: '<b>Canceled Units:</b>',
      value:
        'Units from a customer order that have been canceled at the request of a customer or if a product is out of stock indefinitely, postponed, or out of print.',
    },
    {
      label: '<b>Credit Hold Units:</b>',
      value:
        "Units from a customer’s order that remain on backorder due to a customer's credit status.",
    },
    {
      label: '<b>Company Name:</b>',
      value:
        'Name of company associated with the purchase order selected on the Summary Web page.',
    },
    {
      label: '<b>Format:</b>',
      value:
        'Medium at which a work is published which is usually either HC (hardcover), TP (trade paperback), MM (mass market paperback), or CD (Compact Disc).',
    },
    {
      label: '<b>Imprint:</b>',
      value: 'Brand name under which a work is published.',
    },
    {
      label: '<b>ISBN 10:</b>',
      value:
        '10 digit product identifier. Every product has a unique identifier.',
    },
    {
      label: '<b>ISBN 13:</b>',
      value:
        '13 digit product identifier. Every product has a unique identifier.',
    },
    {
      label: '<b>Number of Titles:</b>',
      value: "Number of distinct ISBNs on a customer's purchase order.",
    },
    {
      label: '<b>On Pick Units:</b>',
      value:
        'Units from a customer order that are in the process of being picked and packed at an MPS warehouse, or, in the case of bindery shipments, at a printer.',
    },
    {
      label: '<b>Order Entry Date:</b>',
      value: 'Date on which the order was placed.',
    },
    {
      label: '<b>Purchase Order Number (PO):</b>',
      value:
        "Unique number generated by a customer that is associated with the customer's order.",
    },
    {
      label: '<b>Pro Number:</b>',
      value: 'Assigned number generally associated with truck load shipments.',
    },
    {
      label: '<b>Shipped Units:</b>',
      value:
        'Units from a customer order that have shipped from an MPS warehouse, or, in the case of bindery shipments, from a printer.',
    },
    {
      label: '<b>Shipper:</b>',
      value: 'Carrier transporting the shipment.',
    },
    {
      label: '<b>Status Date:</b>',
      value: 'A status of the order with a date for the appropriate status.',
    },
    {
      label: '<b>Status:</b>',
      value: 'A status of the order.',
    },
    {
      label: '<b>Title:</b>',
      value: 'Title of book.',
    },
  ],
};

const getSectionContents = (
  columns: string[],
  contentName: keyof typeof SECTIONS_CONTENT
): SectionContent[] => {
  const source = SECTIONS_CONTENT[contentName] || [];
  const list = source.map(item => {
    const id = Math.random();
    if ('label' in item) {
      const colItem: ColumnDefinitionItem = {...item, id};
      return colItem;
    }
    const poItem: PurchaseOrderItem = {...item, id};
    return poItem;
  });

  return [{formatters, columns, list}];
};

export const SECTIONS: Record<string, AccordionPageSection<SectionContent>> = {
  [SECTIONS_TYPES.PURCHASE_ORDER]: {
    title: 'Purchase Order Summary Search Options',
    iconClassName: 'fa-file-invoice-dollar',
    className: 'Purchase-order',
    component: DataList,
    contents: getSectionContents(['value'], SECTIONS_TYPES.PURCHASE_ORDER),
  },
  [SECTIONS_TYPES.COLUMNS_DEFINITION]: {
    title: 'See column definitions below',
    iconClassName: 'fa-table-columns',
    className: 'Columns-definition',
    component: DataList,
    contents: getSectionContents(
      ['label', 'value'],
      SECTIONS_TYPES.COLUMNS_DEFINITION
    ),
  },
};
